import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultKSK2010_R01Dto, KSK2010_R01Dto } from './Dto_KSK2010_R01';
import { DateRange } from 'react-day-picker';
import { getUserSearchFilterDefaults } from 'src/hooks/useInitUserSearchFilter';
import { showWarning } from 'src/utils/toast/toastUtil';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';

export interface KSK2010_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	jobTeam: string;
	closeTag: string;
	orderEmp: string;
	orderEmpNm: string;
}

export interface Store_KSK2010_R01 {
	KSK2010_R01Filter: KSK2010_R01Filter;
	KSK2010_R01PastFilter: KSK2010_R01Filter;
	setKSK2010_R01Filter: (filter: Partial<KSK2010_R01Filter>) => void;
	setKSK2010_R01PastFilter: (filter: Partial<KSK2010_R01Filter>) => void;

    KSK2010_R01List: KSK2010_R01Dto[];
    setKSK2010_R01List: (userList: KSK2010_R01Dto[]) => void;
    insertKSK2010_R01List: (data: KSK2010_R01Dto[], index?: number) => void;
    removeKSK2010_R01List: (index: number) => void;
    updateKSK2010_R01List: (index: number, data: KSK2010_R01Dto) => void;

    deletedKSK2010_R01List: string[];
    addDeletedKSK2010_R01List: (idList: string[]) => void;
    removeDeletedKSK2010_R01List: (idList?: string[]) => void;

	selectedKSK2010_R01List: KSK2010_R01Dto | undefined;
	setSelectedKSK2010_R01List: (data: KSK2010_R01Dto | undefined) => void;
	initSelectedKSK2010_R01List: () => void;

	originalKSK2010_R01List: KSK2010_R01Dto[];
	setOriginalKSK2010_R01List: (list: KSK2010_R01Dto[]) => void;

    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

	allCount: number;					setAllCount: (allCount: number) => void;
	
	isDisabled: (data: KSK2010_R01Dto) => boolean;

    initData: () => void;
}

export const createDefaultKSK2010_R01Filter = (): KSK2010_R01Filter => ({
	...getUserSearchFilterDefaults(),
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	jobTeam: '',
	closeTag: 'N',
	orderEmp: '',
	orderEmpNm: '',
});

export const defaultKSK2010_R01Filter: KSK2010_R01Filter = createDefaultKSK2010_R01Filter();

export const useKSK2010_R01Store = create<Store_KSK2010_R01>((set) => ({
	KSK2010_R01Filter: defaultKSK2010_R01Filter,
	KSK2010_R01PastFilter: defaultKSK2010_R01Filter,
	setKSK2010_R01PastFilter: (filter: Partial<KSK2010_R01Filter>) => set((state) => ({
		...state,
		KSK2010_R01PastFilter: { ...state.KSK2010_R01PastFilter, ...filter }
	})),
	setKSK2010_R01Filter: (filter: Partial<KSK2010_R01Filter>) => set((state) => ({
		...state,
		KSK2010_R01Filter: { ...state.KSK2010_R01Filter, ...filter }
	})),

    KSK2010_R01List: [],
    setKSK2010_R01List: (KSK2010_R01List: KSK2010_R01Dto[]) => set((state) => {
		let newList: KSK2010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		KSK2010_R01List.forEach((item) => {
			newList.push({ 
				...defaultKSK2010_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
				disabled: state.isDisabled(item)
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			KSK2010_R01List: newList
		}
	}),
	insertKSK2010_R01List: (data: KSK2010_R01Dto[], index?: number) => set((state) => {
		let newKSK2010_R01List = [...state.KSK2010_R01List];

		let targetRowData = newKSK2010_R01List.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		let maxValueIndex = Math.max(0, ...newKSK2010_R01List.map(item => item.valueIndex || 0)) + 1;

		const targetRows = newKSK2010_R01List.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		newKSK2010_R01List = newKSK2010_R01List.filter(item => item.orderIndex !== targetOrderIndex);
		newKSK2010_R01List.push(...targetRows);
		
		const newData = data.map(item => ({ ...defaultKSK2010_R01Dto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));
		if (index !== undefined) {
			newKSK2010_R01List.splice(index, 0, ...newData);
		} else {
			newKSK2010_R01List.push(...newData);
		}		

		return {
			...state,
			KSK2010_R01List: newKSK2010_R01List
		}
	}),
	removeKSK2010_R01List: (index: number) => set((state) => {
		const newKSK2010_R01List = [...state.KSK2010_R01List].filter((item) => item.valueIndex !== index);
		return {
			...state,
			KSK2010_R01List: newKSK2010_R01List
		}
	}),
	updateKSK2010_R01List: (valueIndex: number, data: KSK2010_R01Dto) => set((state) => {
		if(data.orderQty < 0) {
			showWarning('수량은 0보다 작을 수 없습니다.');
			data.orderQty = 0;
		}
		const _KSK2010_R01List = state.KSK2010_R01List.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		return {
			...state,
			KSK2010_R01List: _KSK2010_R01List
		}
	}),

    deletedKSK2010_R01List: [],
    addDeletedKSK2010_R01List: (idList: string[]) => set((state) => {
        const _deletedUserList = [...state.deletedKSK2010_R01List];
		
		for(const valueIndex of idList) {
			const isInserted = state.KSK2010_R01List.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.KSK2010_R01List.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedUserList.push(valueIndex);
			}
		}

        return {
            ...state,
            deletedKSK2010_R01List: _deletedUserList
        }
    }),
    removeDeletedKSK2010_R01List: (idList?: string[]) => set((state) => ({
        ...state,
        deletedKSK2010_R01List: [...state.deletedKSK2010_R01List].filter(value => idList ? !idList.includes(value) : false)
    })),

	selectedKSK2010_R01List: undefined,
	setSelectedKSK2010_R01List: (data: KSK2010_R01Dto | undefined) => set({ selectedKSK2010_R01List: data }),
	initSelectedKSK2010_R01List: () => set({ selectedKSK2010_R01List: undefined }),

	originalKSK2010_R01List: [],
	
    setOriginalKSK2010_R01List: (KSK2010_R01List: KSK2010_R01Dto[]) => set((state) => {
		let newList: KSK2010_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		KSK2010_R01List.forEach((item) => {
			newList.push({ 
				...defaultKSK2010_R01Dto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalKSK2010_R01List: newList
		}
	}),

    columnState: null,
    setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),

	allCount: 0,				setAllCount: (allCount: number) => set({ allCount }),

	isDisabled: (data: KSK2010_R01Dto) => data.closeTag === 'Y' || data.resultQty > 0,

    initData: () => set((state) => ({
        ...state,
		KSK2010_R01Filter: createDefaultKSK2010_R01Filter(),
		KSK2010_R01PastFilter: createDefaultKSK2010_R01Filter(),
        KSK2010_R01List: [],
        selectedKSK2010_R01List: undefined,
        deletedKSK2010_R01List: [],
		allCount: 0
    }))
}));
